import React, { useCallback, useState, useEffect } from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { customValidations } from '../../../utils/validation';
import { formatPhone, generateDataTestId } from '../../../utils';

interface PhoneNumberProps {
  onValidation?: (validation: any) => void;
  onChange: (numericValue: string, validation: any) => void;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showInlineError?: boolean;
  phoneValue: string;
  textFieldProps: any;
  fieldLabel: string;
}

const PhoneNumber: React.FC<PhoneNumberProps> = React.memo(
  ({
    onChange,
    onBlur,
    onValidation,
    phoneValue,
    showInlineError,
    textFieldProps,
    fieldLabel,
  }) => {
    const [numericPhone, setNumericPhone] = useState('');
    const [formattedPhone, setFormattedPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');

    const getPhoneNumber = useCallback(
      (value: string) => {
        const normalizedPhone = formatPhone(value);
        setFormattedPhone(normalizedPhone);
        return {
          formattedPhone: normalizedPhone || '',
          numericVal: normalizedPhone?.replace(/\D/g, ''),
        };
      },
      [setFormattedPhone],
    );

    const handlePhoneBlur = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onValidation) {
          const validation = customValidations._validatePhone(numericPhone);
          setPhoneError(validation.errorMessage ?? '');
          onValidation(validation);
        }
        if (onBlur) {
          onBlur(event);
        }
      },
      [onBlur, onValidation, numericPhone],
    );

    const handlePhoneChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const { formattedPhone, numericVal } = getPhoneNumber(newValue);
        setFormattedPhone(formattedPhone);
        setNumericPhone(numericVal);

        const validation = customValidations._validatePhone(numericVal);
        setPhoneError(validation.errorMessage ?? '');
        onChange(numericVal, validation);
      },
      [getPhoneNumber, onChange, setFormattedPhone],
    );

    useEffect(() => {
      const { formattedPhone } = getPhoneNumber(phoneValue);
      setFormattedPhone(formattedPhone);
    }, [getPhoneNumber, phoneValue]);

    return (
      <Grid>
        <TextField
          data-testid={generateDataTestId(fieldLabel, undefined, 'phoneNumber')}
          {...textFieldProps}
          value={formattedPhone}
          onChange={handlePhoneChange}
          onBlur={handlePhoneBlur}
        />
        {phoneError && !!showInlineError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <ErrorIcon color="error" sx={{ mr: 1, fontSize: 18 }} />
            <Typography color="error" variant="caption">
              {phoneError}
            </Typography>
          </Box>
        )}
      </Grid>
    );
  },
);

export default PhoneNumber;
